import { useEffect, useMemo, useState } from 'react'

import useParams from 'commons/customHooks/useParams'
import i18n from 'commons/libs/i18n'
import { getPreviousYearDate, oneYearApart } from 'commons/utils/date'
import { downloadFile, FileType } from 'commons/utils/file'
import { Alert } from 'components/Alert'
import { Button, DSButtonType } from 'components/Buttons'
import { defaultValues } from 'components/Filters'
import { DatePicker, Radio } from 'components/Form'
import { LoadingHavaianas } from 'components/Loading'
import type { ViewByType } from 'container/ManageOrders'
import { type FormatBodyType, formatRequestBodyByFilters } from 'container/ManageOrders/utils'
import dayjs from 'dayjs'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { downloadManagementReport } from 'services/manage_ov'
import type { RequestOVsByFilters } from 'services/manage_ov/interface'

import * as S from './styles'

export type PeriodFormProps = {
  initialQuota: string
  finalQuota: string
}

type ModalReportProps = {
  open: boolean
  setOpen: (value: boolean) => void
  viewBy: ViewByType
}

export enum ReportDownloadOptions {
  CURRENT_FILTER = 'currentFilter',
  CUSTOM_PERIOD = 'customPeriod',
}

export type ReportDownloadOptionsType = `${ReportDownloadOptions}`

// TODO: replace direct dayjs() usage with utils

const ModalReport = ({ open, setOpen, viewBy }: ModalReportProps) => {
  const [selectedOption, setSelectedOption] = useState<ReportDownloadOptionsType>(
    ReportDownloadOptions.CURRENT_FILTER,
  )
  const [isLoading, setIsLoading] = useState(false)
  const { control, handleSubmit, reset, getValues, formState, trigger } = useForm<PeriodFormProps>({
    defaultValues: {
      finalQuota: new Date().toString(),
      initialQuota: getPreviousYearDate(new Date()),
    },
    mode: 'onChange',
  })

  const { getParams } = useParams()
  const getSearchParams = (): FormatBodyType => ({ ...defaultValues, ...getParams() })

  const handleClose = () => {
    reset()
    setSelectedOption(ReportDownloadOptions.CURRENT_FILTER)
    setOpen(false)
  }

  const getReport = async (data: RequestOVsByFilters) => {
    try {
      setIsLoading(true)

      const file = await downloadManagementReport(data)
      const fileName = 'relatorio_matriz_ov.xlsx'

      downloadFile({ file, fileName, fileType: FileType.Excel })

      toast.success(i18n.t('common.messages.reportDownloadedSuccessfully'))
    } catch {
      toast.error(i18n.t('common.errors.unableToDownloadReport'))
    } finally {
      setIsLoading(false)
      handleClose()
    }
  }
  const handleDownloadReport = async (data: PeriodFormProps) => {
    const searchParams = getSearchParams()
    const payload =
      selectedOption === ReportDownloadOptions.CURRENT_FILTER
        ? formatRequestBodyByFilters(searchParams, false, viewBy, true)
        : {
            ...formatRequestBodyByFilters(searchParams, false, viewBy, true),
            dataFinal: new Date(data.finalQuota).toISOString(),
            dataInicial: new Date(data.initialQuota).toISOString(),
          }

    await getReport(payload)
  }

  const isValidInterval = useMemo(() => {
    const { initialQuota, finalQuota } = getValues()
    if (formState.errors.initialQuota || formState.errors.finalQuota) return true
    return oneYearApart(new Date(initialQuota), new Date(finalQuota))
  }, [formState])

  useEffect(() => {
    if (selectedOption === ReportDownloadOptions.CURRENT_FILTER) reset()
  }, [selectedOption])

  return (
    open && (
      <ModalContentLayout
        closeAction={handleClose}
        closeButton
        title={i18n.t('buttons.downloadReport')}
        titleSize={24}
      >
        {!isLoading ? (
          <S.Content onSubmit={handleSubmit(handleDownloadReport)}>
            <Radio
              checked={selectedOption === ReportDownloadOptions.CURRENT_FILTER}
              label={i18n.t('buttons.downloadSearchResults')}
              name="report_type"
              onChange={() => setSelectedOption(ReportDownloadOptions.CURRENT_FILTER)}
              value={ReportDownloadOptions.CURRENT_FILTER}
            />
            <Radio
              checked={selectedOption === ReportDownloadOptions.CUSTOM_PERIOD}
              label={i18n.t('buttons.downloadCustomPeriod')}
              name="report_type"
              onChange={() => setSelectedOption(ReportDownloadOptions.CUSTOM_PERIOD)}
              value={ReportDownloadOptions.CUSTOM_PERIOD}
            />

            {selectedOption === ReportDownloadOptions.CUSTOM_PERIOD && (
              <S.WrapChoosePeriod>
                <p>{i18n.t('common.validations.customPeriodMaxOneYear')}</p>
                <S.WrapMonthInputs>
                  <Controller
                    control={control}
                    name="initialQuota"
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        disableMaskedInput={false}
                        error={fieldState.error?.message}
                        errorState={!isValidInterval}
                        inputFormat="MM/YYYY"
                        label={i18n.t('common.initialQuotaMonth')}
                        maxDate={dayjs().add(5, 'year') as unknown as string}
                        minDate={dayjs().subtract(5, 'year') as unknown as string}
                        onChange={(date) => {
                          field.onChange(dayjs(date).format('YYYY-MM'))
                          trigger()
                        }}
                        views={['year', 'month']}
                      />
                    )}
                    rules={{
                      validate: {
                        min: (date) =>
                          dayjs(date)
                            .endOf('month')
                            .isAfter(dayjs().subtract(5, 'year').startOf('month')) ||
                          i18n.t('common.validations.invalidDate'),
                      },
                    }}
                  />
                  <Controller
                    control={control}
                    name="finalQuota"
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        disableMaskedInput={false}
                        error={fieldState.error?.message}
                        errorState={!isValidInterval}
                        inputFormat="MM/YYYY"
                        label={i18n.t('common.finalQuotaMonth')}
                        maxDate={dayjs().add(5, 'year') as unknown as string}
                        minDate={dayjs().subtract(5, 'year') as unknown as string}
                        onChange={(date) => {
                          field.onChange(dayjs(date).format('YYYY-MM'))
                          trigger()
                        }}
                        views={['year', 'month']}
                      />
                    )}
                    rules={{
                      validate: {
                        afterBegin: (date) =>
                          dayjs(date)
                            .endOf('month')
                            .isAfter(dayjs(getValues('initialQuota')).startOf('month')) ||
                          i18n.t('common.validations.invalidPeriod'),
                        min: (date) =>
                          dayjs(date)
                            .endOf('month')
                            .isAfter(dayjs().subtract(5, 'year').startOf('month')) ||
                          i18n.t('common.validations.invalidDate'),
                      },
                    }}
                  />
                </S.WrapMonthInputs>
                {!isValidInterval && (
                  <Alert
                    fullWidth
                    kind="error"
                    title={i18n.t('common.validations.periodMustBeLessThanOneYear')}
                  />
                )}
              </S.WrapChoosePeriod>
            )}

            <S.WrapButtons>
              <Button
                content={i18n.t('buttons.back')}
                design={DSButtonType.TEXT_BUTTON}
                fullWidth={false}
                height={37}
                onClick={handleClose}
                type="button"
              />
              <Button
                content={i18n.t('buttons.download')}
                design={DSButtonType.SM_PRIMARY_BUTTON}
                disabled={!isValidInterval || !!formState.errors?.finalQuota}
                fullWidth={false}
                height={37}
                onClick={handleSubmit(handleDownloadReport)}
              />
            </S.WrapButtons>
          </S.Content>
        ) : (
          <LoadingHavaianas />
        )}
      </ModalContentLayout>
    )
  )
}

export default ModalReport
